/**
 * WS-NAV — the active workspace's tree.
 *
 * A flat, one-level list: the workspace IS the grouping, so the pre-refactor
 * expand/collapse disclosure groups are gone. Long trees (Administration) use
 * lightweight section captions instead of collapsible groups — a caption
 * costs no clicks.
 *
 * Rendered by both the desktop sidebar and the mobile drawer; `dense={false}`
 * gives the drawer 48 px touch rows (mobile_strategy §2.4).
 */
import type { ReactElement } from "react";
import { Box, Link as MuiLink, Tooltip } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { typography } from "@aeriosiq/ui";
import { useActiveWorkspace } from "../workspaces/useWorkspaces.js";
import { deviceForPath } from "../workspaces/routeDevice.js";
import { navLeafIcon } from "../workspaces/navIcons.js";
import {
  LEAF_HEIGHT,
  ROW_HEIGHT,
  TOUCH_ROW_HEIGHT,
  isNavItemCurrent,
  rowStyles,
} from "./primitives.js";
import type { WorkspaceNavItem } from "../workspaces/types.js";

interface Props {
  /** Touch density + a close callback: the mobile drawer. */
  touch?: boolean;
  onNavigate?: (() => void) | undefined;
}

function NavRow({
  item,
  touch,
  onNavigate,
  accent,
  siblings,
}: {
  item: WorkspaceNavItem;
  touch: boolean;
  onNavigate?: (() => void) | undefined;
  accent: "platform" | undefined;
  /** The workspace's other leaves (a deeper sibling wins its own subtree). */
  siblings: readonly (string | undefined)[];
}): ReactElement {
  const { pathname } = useLocation();
  const enabled = item.enabled !== false && item.to !== undefined;
  const selected = enabled && isNavItemCurrent(pathname, item.to, siblings);

  const styles = rowStyles({
    selected,
    collapsed: false,
    height: touch ? TOUCH_ROW_HEIGHT : LEAF_HEIGHT,
    // 12px here + the 8px li gutter the pill needs = text at 20px, which is
    // where the section captions and the panel heading also sit. Before this
    // the rows were at 24 and the captions at 16 — two different columns
    // inside one list, which is the same defect as the old brand header at
    // 16 against everything else at 12, just one level further down.
    pl: 1.5,
    ml: 0,
    mr: 0,
    accent,
  });

  // WS-ICON-01 — one glyph per leaf (Christian, 2026-08-30: "per leaf, no icon
  // per section, take the admin page as reference").
  //
  // The story argued leaves had no room at LEAF_HEIGHT 32. They do: the console
  // sits a 15px glyph in its own leaf rows, and the row here is already
  // `display:flex` with `gap: 1.5`, so the icon costs no vertical space at all.
  //
  // A leaf with no mapped route (placeholders have no `to`) renders a spacer of
  // the SAME width instead of nothing — otherwise adding a section would shift
  // every label beside it, which is a layout regression disguised as a missing
  // entry (AC-4). `aria-hidden` throughout: the link's `aria-label` is the
  // accessible name and must not change (AC-2).
  const Icon = navLeafIcon(item.to);
  const iconNode = Icon ? (
    <Icon
      fontSize="small"
      aria-hidden="true"
      sx={{
        flex: "0 0 16px",
        width: 16,
        height: 16,
        opacity: enabled ? 0.85 : 0.4,
      }}
    />
  ) : (
    <Box
      component="span"
      aria-hidden="true"
      sx={{ flex: "0 0 16px", width: 16 }}
    />
  );

  const labelNode = (
    <Box
      component="span"
      // WS-RAIL — the panel is 208px where the old sidebar was 272, so a long
      // label that used to fit now ellipsizes ("Defect Type Collections" is the
      // first to go). The accessible name is unaffected — `aria-label` on the
      // link carries the full string either way — but a sighted user was losing
      // the tail with no way to recover it, so the native title restores it on
      // hover. Cheaper and more reliable than measuring for truncation.
      title={item.label}
      sx={{
        flex: 1,
        fontSize: typography.size["body-md"],
        letterSpacing: typography.tracking["body-md"],
        fontWeight: selected
          ? typography.weight.medium
          : typography.weight.regular,
        color: enabled ? "inherit" : "text.disabled",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {item.label}
    </Box>
  );

  if (enabled) {
    return (
      <Box component="li" sx={{ listStyle: "none", px: 1 }}>
        <MuiLink
          component={RouterLink}
          to={item.to}
          aria-label={item.label}
          // Stable hook for spec selectors that walk the tree by route.
          data-nav-id={item.to}
          onClick={onNavigate}
          {...(selected ? { "aria-current": "page" as const } : {})}
          sx={styles}
        >
          {iconNode}
          {labelNode}
        </MuiLink>
      </Box>
    );
  }

  return (
    <Box component="li" sx={{ listStyle: "none", px: 1 }}>
      <Tooltip title="Coming soon" placement="right" disableInteractive>
        <Box
          component="span"
          role="link"
          tabIndex={0}
          aria-disabled="true"
          aria-label={item.label}
          {...(item.to !== undefined ? { "data-nav-id": item.to } : {})}
          sx={styles}
        >
          {iconNode}
          {labelNode}
        </Box>
      </Tooltip>
    </Box>
  );
}

function SectionCaption({
  label,
  first,
}: {
  label: string;
  first: boolean;
}): ReactElement {
  return (
    <Box
      component="li"
      // Deliberately NOT role="presentation": axe's `list` rule rejects a
      // presentational direct child of a <ul>, and the caption is real
      // structure a screen-reader user benefits from hearing.
      sx={{
        listStyle: "none",
        // 20px — the row text's inset, not the pill's edge. See NavRow.
        px: 2.5,
        // Christian, 2026-08-14: Administration's three groups (Access,
        // Settings, Configuration) were "visually hard to separate", and asked
        // whether more space would fight the design system.
        //
        // It does the opposite — the system already answers this and we were
        // spending HALF of it. `--group-gap` is 24px, and it is 24px in BOTH
        // densities: compact compresses `--control-gap` (12→8) and
        // `--field-label-gap` (8→0) but deliberately does NOT compress the gap
        // between labelled groups. Densifying a surface is allowed to pack
        // controls tighter; it is not allowed to stop you seeing where one
        // group ends. This was 12px.
        //
        // The first caption gets no leading gap — there is no group above it to
        // separate from, and the list's own `py` already sets it off the header.
        pt: first ? 0.5 : "var(--group-gap, 24px)",
        pb: 0.5,
        // D6/D77's label tier, which this was a near-miss of: 12px Inter at
        // +0.04em, where the decided label is 11px mono caps at +0.14em. A
        // section caption IS a label — it labels the group beneath it — so it
        // takes the recipe. Capitals need MORE tracking, not less, and the mono
        // face separates a caption from the nav rows by more than a weight step,
        // which is the other half of "hard to separate".
        ...typography.label,
        color: "text.secondary",
      }}
    >
      {label}
    </Box>
  );
}

export function WorkspaceNav({
  touch = false,
  onNavigate,
}: Props): ReactElement | null {
  const { workspace, items } = useActiveWorkspace();
  // There is no `collapsed` branch any more, and that is the point. This
  // component used to return `null` when the sidebar was collapsed, which
  // deleted page navigation rather than densifying it. Collapsing is now the
  // panel's business — it unmounts this whole column and the rail carries on.
  if (!workspace) return null;

  const accent = workspace.accent === "platform" ? "platform" : undefined;

  // mobile_strategy §2.2 — a desktop-only destination is not offered in the
  // phone drawer at all. The route still exists: deep-linking to it renders
  // the DesktopOnly card rather than 404-ing.
  const shown = touch
    ? items.filter(
        (item) => item.to === undefined || deviceForPath(item.to) === "all",
      )
    : items;

  const siblingTos = items.map((i) => i.to);
  let lastSection: string | undefined;
  const rows: ReactElement[] = [];
  for (const item of shown) {
    if (item.section !== undefined && item.section !== lastSection) {
      lastSection = item.section;
      rows.push(
        <SectionCaption
          key={`s-${item.section}`}
          label={item.section}
          first={rows.length === 0}
        />,
      );
    }
    rows.push(
      <NavRow
        key={item.label}
        item={item}
        touch={touch}
        onNavigate={onNavigate}
        accent={accent}
        siblings={siblingTos}
      />,
    );
  }

  return (
    <Box
      component="ul"
      aria-label={`${workspace.label} navigation`}
      sx={{
        m: 0,
        p: 0,
        listStyle: "none",
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        py: 1,
        minHeight: ROW_HEIGHT,
      }}
    >
      {rows}
    </Box>
  );
}
